import { useCallback } from 'react';

import { MarginOrdersQuery } from '@swyftx/margin/build/types';

import { api } from '@shared/api';
import { TimesEnum } from '@shared/enums';
import { UserStore } from '@shared/store';

import { useInfiniteQuery } from '@tanstack/react-query';

import { useMarginAccount } from '../account';

type Props = {
  pageSize?: number;
  side?: string;
  status?: string[];
  marketUuid?: string;
  sortBy?: MarginOrdersQuery['sortBy'];
  sortDir?: MarginOrdersQuery['sortDir'];
};

const useFetchMarginOrders = (
  { pageSize = 25, side, status, marketUuid, sortBy, sortDir }: Props = { pageSize: 25, sortBy: 'dateTime' },
) => {
  const { account } = useMarginAccount();
  const { getUniqueIdentifier } = UserStore.useUserStore;

  const fetchOrders = useCallback(
    async (pageNumber = 1) => {
      const response = await api.endpoints.getMarginOrders({
        params: { swyftxUserId: getUniqueIdentifier(), traderAccountId: account?.traderAccountId },
        query: {
          pageNumber,
          pageSize,
          side,
          status: status?.join(','),
          marketUuid,
          sortBy,
          sortDir,
        },
      });

      return {
        orders: response.data.orders,
        page: response.data.orders.length === pageSize ? pageNumber : undefined,
      };
    },
    [getUniqueIdentifier, account?.traderAccountId, pageSize, side, status, marketUuid, sortBy, sortDir],
  );

  return useInfiniteQuery({
    queryKey: [
      'margin-orders',
      getUniqueIdentifier(),
      account?.traderAccountId,
      status,
      side,
      marketUuid,
      sortBy,
      sortDir,
    ],
    queryFn: ({ pageParam = 1 }) => fetchOrders(pageParam),
    keepPreviousData: true,
    staleTime: TimesEnum.SECOND * 60,
    refetchInterval: TimesEnum.SECOND * 60,
    retry: 3,
    refetchOnWindowFocus: false,
    networkMode: 'always',
    enabled: !!getUniqueIdentifier() && !!account?.traderAccountId,
    getNextPageParam: (lastPage) => (lastPage.page !== undefined ? lastPage.page + 1 : undefined),
    getPreviousPageParam: (lastPage) => (lastPage.page !== undefined ? lastPage.page - 1 : undefined),
  });
};

export { useFetchMarginOrders };
